import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Navigation-safe page polling. Reloads the current page on an interval until
// the operator leaves it. Use this INSTEAD of `<meta http-equiv="refresh">`:
// a native meta-refresh schedules a browser-level timer that Turbo Drive does
// NOT cancel when you navigate to another page, so it fires later and yanks you
// back to this URL. Because Stimulus runs `disconnect()` when Turbo swaps the
// body on navigation, the timer here is always cleared the moment you leave.
//
// INVARIANT: connect() may run more than once per logical page view (Turbo
// renders a cached preview, then the fresh response). disconnect() pairs with
// each connect(), so at most one timer is live — but never give this controller
// a non-idempotent side effect. Re-visiting the same URL is the only safe action.
//
// Usage:
//   <div data-controller="poll"></div>                       (3s default)
//   <div data-controller="poll" data-poll-interval-value="5000"></div>
export default class extends Controller {
  static values = { interval: { type: Number, default: 3000 } }

  connect() {
    // Snapshot the URL at arm time, not fire time. A `button_to` POST on this
    // page (Skip/Discard/Regenerate/…) redirects via Turbo; if the timer fires
    // in that brief window before the body swaps, reading `window.location.href`
    // live could re-visit the page the operator is leaving. Polling the URL we
    // connected on — and only if we're still on it — keeps the poll honest.
    const url = window.location.href
    this.timer = setTimeout(() => {
      if (window.location.href === url) {
        Turbo.visit(url, { action: "replace" })
      }
    }, this.intervalValue)
  }

  disconnect() {
    clearTimeout(this.timer)
  }
}
